/* ==========================================================================
   OUTLETZONE — collection-filters.js
   Progressive enhancement over Shopify's native filtering.

   The facet form is a real <form method="get">. Without JS it submits and the
   page reloads with the correct filters applied. With JS we intercept, fetch
   the same URL, and swap only the grid and the facet panel — so the URL stays
   canonical, shareable and crawlable either way.
   ========================================================================== */
(function () {
  'use strict';

  const OZ = window.OZ;
  const { $, $$, debounce, announce } = OZ.helpers;

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('input', debounce((e) => {
        // Price inputs need a longer settle; checkboxes apply immediately.
        if (e.target.type === 'number') this.apply();
      }, 600));

      this.form.addEventListener('change', (e) => {
        if (e.target.type === 'number') return;
        this.apply();
      });

      this.addEventListener('click', (e) => {
        const clear = e.target.closest('[data-facet-clear]');
        if (clear) {
          e.preventDefault();
          this.navigate(clear.href);
        }
      });

      window.addEventListener('popstate', () => this.navigate(location.href, false));
    }

    apply() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value !== '') params.append(key, value);
      }
      const url = `${location.pathname}?${params.toString()}`;
      this.navigate(url);
    }

    setBusy(state) {
      const grid = document.getElementById('ProductGridContainer');
      if (grid) {
        grid.setAttribute('aria-busy', state ? 'true' : 'false');
        grid.style.opacity = state ? '0.5' : '';
      }
    }

    async navigate(url, push = true) {
      this.setBusy(true);
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error();
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.swap(doc, '#ProductGridContainer');
        this.swap(doc, '#FacetsDesktop');
        this.swap(doc, '#FacetsMobile');
        this.swap(doc, '#ActiveFacets');
        this.swap(doc, '[data-results-count]');

        if (push) window.history.pushState({}, '', url);

        const count = $('[data-results-count]');
        if (count) announce(count.textContent.trim());

        const grid = document.getElementById('ProductGridContainer');
        if (grid) {
          const top = grid.getBoundingClientRect().top + window.scrollY - 120;
          if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
        }
      } catch (_) {
        // Network failure: fall back to a real navigation rather than
        // leaving the customer looking at stale results.
        window.location.href = url;
      } finally {
        this.setBusy(false);
      }
    }

    swap(doc, selector) {
      const next = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (next && current) current.innerHTML = next.innerHTML;
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ========================================================= SORT SELECT   */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-sort-by]');
    if (!select) return;
    const params = new URLSearchParams(location.search);
    params.set('sort_by', select.value);
    const filters = document.querySelector('facet-filters');
    const url = `${location.pathname}?${params.toString()}`;
    filters ? filters.navigate(url) : (location.href = url);
  });

  /* ======================================================= LOAD MORE       */
  // Pagination links remain in the DOM and remain crawlable; this simply
  // appends the next page in place for customers who prefer to keep scrolling.
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => this.load());
    }

    async load() {
      const next = this.dataset.nextUrl;
      if (!next) return;
      this.button.classList.add('btn--loading');
      try {
        const res = await fetch(next);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = document.getElementById('ProductGrid');
        const newCards = doc.querySelectorAll('#ProductGrid > *');
        newCards.forEach((c) => grid.appendChild(c));

        const nextLoader = doc.querySelector('load-more');
        if (nextLoader && nextLoader.dataset.nextUrl) {
          this.dataset.nextUrl = nextLoader.dataset.nextUrl;
        } else {
          this.remove();
        }
        window.history.replaceState({}, '', next);
        announce(`${grid.children.length}`);
      } catch (_) {
        window.location.href = next;
      } finally {
        this.button?.classList.remove('btn--loading');
      }
    }
  }
  customElements.define('load-more', LoadMore);
})();
